<template>
    <div class="bg-grey-lightest">
        <section id="heading" class="pt-12 pb-24">
            <!-- Header Nav -->
            <header-nav ref="headerNav"></header-nav>
        </section>
        <section class="py-12">
            <div class="max-w-sm mx-auto px-6">
                <patient-loading v-if="! office"></patient-loading>
                <div class="rounded bg-white shadow relative -mt-24" v-else>
                    <router-link :to="{ path: '/' }" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                        <span class="icon"><i class="mdi mdi-close"></i></span>
                    </router-link>
                    <div class="p-6">
                        <div class="mb-4 flex">
                            <avatar :avatar="office.doctorAvatar" :name="office.doctorFirstName + ' ' + office.doctorLastName" class="mr-4 w-10 h-10"></avatar>
                            <div>
                                <div class="text-black text-lg font-semibold mb-1">{{ office.doctorFirstName }} {{ office.doctorLastName }}</div>
                                <div class="text-green font-semibold text-sm mb-1" v-if="office.isDoctorRegulated && (office.doctorType !== 'OSTEOPATH' && office.doctorType !== 'PSYCHOLOGIST')"><i class="mdi mdi-currency-eur"></i> {{ $t('Contracted_doctor') }}</div>
                                <div class="text-red font-semibold text-sm mb-1" v-if="!office.isDoctorRegulated && (office.doctorType !== 'OSTEOPATH' && office.doctorType !== 'PSYCHOLOGIST')"><i class="mdi mdi-currency-eur"></i> {{ $t('Contracted_doctor_negative') }}</div>
                                <div v-if="office.isDoctorMemberOfOsteopathie" class="tooltip text-green font-semibold text-sm mt-1 mb-2"><i class="mdi mdi-check-circle"></i>
                                    {{ $t('Member_of_Osteopathie') }}
                                <span class="tooltip-text text-sm">{{$t('Oste_tooltip')}}</span>
                                </div>
                                <!-- OFFICE SETTINGS -->
                                <OfficeSettings
                                    v-if="office.officeSettings"
                                    :accessible="office.officeSettings.accessible"
                                    :card="office.officeSettings.card"
                                    :cash="office.officeSettings.cash"
                                />
                                <!-- OFFICE SETTINGS -->
                            </div>
                        </div>
                        <div class="">
                            <div class="text-grey-darkest">
                                {{ office.name }}<br />
                                {{ office.streetNumber }} {{ office.streetName }}<br />
                                {{ office.zipCode }} {{ office.locality }}
                            </div>
                        </div>
                    </div>
                    <gmap-map
                        :center="{lat: office.location._lat, lng: office.location._long }"
                        :zoom="13"
                        :options="{disableDefaultUI: true}"
                        style="height: 250px;"
                        class="w-full">
                        <gmap-marker
                            :position="{lat: office.location._lat, lng: office.location._long }"></gmap-marker>
                    </gmap-map>
                    <div v-if="! loadingTimeslots">
                        <div v-if="Object.keys(timeslots).length">
                            <div class="uppercase tracking-wide text-grey-darker text-sm font-semibold px-3 md:px-6 my-4">{{ $t('Select_an_availability') }}</div>
                            <div class="max-h-timeslots overflow-scroll">
                                <router-link
                                    :to="{ name: 'timeslot.show', params: { id: timeslot.id } }"
                                    v-for="timeslot in sortedTimeslots"
                                    :key="timeslot.id"
                                    class="block w-full border-t px-6 py-3 hover:bg-grey-lightest text-grey-darker">
                                    {{ timeslot.date | momentFormat('YYYY-MM-DD', $t('date_selector_format')) }} {{ $t('at') }} {{ timeslot.time | momentFormatTime($t('time_format')) }}
                                </router-link>
                                <div class="py-1 rounded-b border-t"></div>
                            </div>
                        </div>
                        <div class="px-6 py-4" v-else>
                            <bubble>
                                <p>{{ $t('X_is_not_available_Register_on_the_waiting_list_to_inform_the_dentists_near_Y', {name: office.doctorFirstName + ' ' + office.doctorLastName, location: patientPlace}) }}</p>
                                <p class="mt-2" v-if="! hasEmergencies && ! loadingTimeslots">{{ $t('Activate_the_alert_and_I_will_inform_you_') }}</p>
                                <p class="mt-2" v-if="hasEmergencies && ! loadingTimeslots">{{ $t('You_are_already_in_the_waiting_list') }}</p>
                            </bubble>
                            <waiting-list-button v-if="! hasEmergencies && ! loadingTimeslots"></waiting-list-button>
                        </div>
                    </div>
                </div>
                <dfp-slot
                    v-if="office"
                    path="/21865209613/Patients/OfficeShow"
                    :size="[300,250]"
                    :id="`div-gpt-ad-office`"
                    :medical-practice="office.medicalPractice"
                    class="mt-6" />
            </div>
        </section>
    </div>
</template>

<script>
import firebase from 'firebase/app'
import helpers from '@/mixins/helpers'

import Bubble from '@/components/Patient/Bubble'
import HeaderNav from '@/components/Patient/HeaderNav'
import WaitingListButton from '@/components/Patient/WaitingListButton'
import OfficeSettings from '@/components/Patient/OfficeSettings'

import emergencyRepository from '@/data/EmergencyRepository'
import officeRepository from '@/data/OfficeRepository'
import timeslotRepository from '@/data/TimeSlotRepository'

export default {
    name: 'OfficeShow',

    mixins: [helpers],

    components: {Bubble, HeaderNav, WaitingListButton, OfficeSettings},

    data () {
        return {
            loadingTimeslots: true,
            office: null,
            timeslots: [],
            hasEmergencies: false
        }
    },

    computed: {
        sortedTimeslots () {
            return _.sortBy(this.timeslots, ['date', 'time'])
        }
    },

    mounted () {
        this.fetchOffice()
    },

    methods: {
        showLogin () {
            this.$refs.headerNav.showLogin()
        },

        fetchOffice () {
            this.officeId = this.$route.params.id

            officeRepository.find(this.officeId, async (office) => {
                if (!office.isDoctorCertified) {
                    this.alertPatientError(this.$t('Errors.offices_could_not_be_loaded'))

                    return this.$router.push('/')
                } else {
                    this.office = office
                    const ok = office.isDoctorRegulated && office.doctorType !== 'OSTEOPATH'

                    this.fetchTimeslots()
                    this.fetchEmergencies()
                }
            }, () => {
                this.alertPatientError(this.$t('Errors.offices_could_not_be_loaded'))
            })
        },

        fetchTimeslots () {
            this.loadingTimeslots = true
            timeslotRepository.allForOffice(this.officeId, (timeslots) => {
                this.timeslots = timeslots

                this.loadingTimeslots = false
            }, () => {
                this.alertPatientError(this.$t('Errors.timeslotss_could_not_be_loaded'))

                this.loadingTimeslots = false
            })
        },

        fetchEmergencies () {
            emergencyRepository.allForPatient(this.userId, (emergencies) => {
                this.loadingEmergencies = false
                if (Object.keys(emergencies).length > 0) {
                    this.hasEmergencies = true
                }
            }, () => {
                this.loadingEmergencies = false
            })
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Patient/OfficeShow.vue